import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { SelectInput } from "@/components/ui/Field";
import {
  getPrinterOptions,
  savePrinterOptions,
  type CupsPrinterOption,
  type CupsPrinterOptionsResponse,
} from "../../lib/settingsApi";

const FACTORY_VALUE = "__factory__";

type DraftValues = Record<string, string>;

function buildDraft(options: CupsPrinterOption[]): DraftValues {
  const draft: DraftValues = {};
  for (const option of options) {
    draft[option.key] = option.value ?? option.factory_default ?? option.choices[0]?.value ?? "";
  }
  return draft;
}

function buildSavePayload(
  options: CupsPrinterOption[],
  draft: DraftValues,
  initialDraft: DraftValues,
): Record<string, string | null> {
  const payload: Record<string, string | null> = {};
  for (const option of options) {
    const next = draft[option.key] ?? "";
    const prev = initialDraft[option.key] ?? "";
    if (next === prev) continue;
    payload[option.key] = next === FACTORY_VALUE ? null : next;
  }
  return payload;
}

function formatChoiceLabel(value: string, factoryDefault?: string | null): string {
  if (factoryDefault && value === factoryDefault) {
    return `${value} (Werkseinstellung)`;
  }
  return value;
}

export function PrintPrinterDefaultsSection({ printers }: { printers: string[] }) {
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [data, setData] = useState<CupsPrinterOptionsResponse | null>(null);
  const [draft, setDraft] = useState<DraftValues>({});
  const [initialDraft, setInitialDraft] = useState<DraftValues>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (printers.length && !selectedPrinter) {
      setSelectedPrinter(printers[0] ?? "");
    }
    if (selectedPrinter && !printers.includes(selectedPrinter)) {
      setSelectedPrinter(printers[0] ?? "");
    }
  }, [printers, selectedPrinter]);

  const loadOptions = useCallback(async (printerName: string) => {
    if (!printerName) {
      setData(null);
      setDraft({});
      setInitialDraft({});
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const response = await getPrinterOptions(printerName);
      const nextDraft = buildDraft(response.options ?? []);
      setData(response);
      setDraft(nextDraft);
      setInitialDraft(nextDraft);
    } catch (err) {
      setData(null);
      setDraft({});
      setInitialDraft({});
      setMessage({
        variant: "error",
        text: err instanceof Error ? err.message : "Druckeroptionen konnten nicht geladen werden.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOptions(selectedPrinter);
  }, [loadOptions, selectedPrinter]);

  const dirty = useMemo(() => {
    return Object.keys(draft).some((key) => (draft[key] ?? "") !== (initialDraft[key] ?? ""));
  }, [draft, initialDraft]);

  async function handleSave() {
    if (!selectedPrinter || !data?.options?.length) return;
    const payload = buildSavePayload(data.options, draft, initialDraft);
    if (!Object.keys(payload).length) return;

    setSaving(true);
    setMessage(null);
    try {
      const response = await savePrinterOptions(selectedPrinter, payload);
      const nextDraft = buildDraft(response.options ?? []);
      setData(response);
      setDraft(nextDraft);
      setInitialDraft(nextDraft);
      setMessage({ variant: "success", text: "Standardeinstellungen gespeichert." });
    } catch (err) {
      setMessage({
        variant: "error",
        text: err instanceof Error ? err.message : "Speichern fehlgeschlagen.",
      });
    } finally {
      setSaving(false);
    }
  }

  function handleResetAll() {
    if (!data?.options?.length) return;
    const nextDraft = { ...draft };
    for (const option of data.options) {
      nextDraft[option.key] = FACTORY_VALUE;
    }
    setDraft(nextDraft);
  }

  return (
    <Card
      title="Drucker-Standardeinstellungen"
      description="CUPS-Standardoptionen pro Drucker – gelten für alle Druckaufträge aus der App."
      className="mt-4"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={loading || saving || !selectedPrinter}
            onClick={() => void loadOptions(selectedPrinter)}
          >
            Aktualisieren
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={loading || saving || !data?.options?.length}
            onClick={handleResetAll}
          >
            Alle auf Werkseinstellung
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={loading || saving || !dirty}
            loading={saving}
            onClick={() => void handleSave()}
          >
            Speichern
          </Button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label className="flex min-w-[260px] flex-col gap-1 text-sm">
          <span className="text-label-secondary">Drucker</span>
          <SelectInput value={selectedPrinter} onChange={(e) => setSelectedPrinter(e.target.value)}>
            {printers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </SelectInput>
        </label>
      </div>

      {loading && <Spinner />}

      {message && (
        <Alert variant={message.variant} title={message.variant === "success" ? "Erledigt" : "Fehler"} className="mb-4">
          {message.text}
        </Alert>
      )}

      {!loading && data && (data.options?.length ?? 0) === 0 && (
        <Alert variant="warning" title="Keine Optionen" className="mb-4">
          Für diesen Drucker wurden keine konfigurierbaren CUPS-Optionen gefunden. Prüfen Sie, ob die Queue über IPPS
          mit „everywhere“ eingerichtet ist.
        </Alert>
      )}

      {!loading && !!data?.options?.length && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.options.map((option) => {
            const currentValue = draft[option.key] ?? "";
            return (
              <label key={option.key} className="flex flex-col gap-1 rounded-lg border border-separator px-3 py-2 text-sm">
                <span className="font-medium text-label">{option.label}</span>
                <span className="text-xs text-label-secondary">{option.key}</span>
                {option.choices.length > 0 ? (
                  <SelectInput
                    value={currentValue}
                    onChange={(e) => setDraft((prev) => ({ ...prev, [option.key]: e.target.value }))}
                  >
                    {option.factory_default && (
                      <option value={FACTORY_VALUE}>
                        Werkseinstellung ({option.factory_default})
                      </option>
                    )}
                    {option.choices.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {formatChoiceLabel(choice.value, option.factory_default)}
                      </option>
                    ))}
                  </SelectInput>
                ) : (
                  <SelectInput
                    value={currentValue}
                    onChange={(e) => setDraft((prev) => ({ ...prev, [option.key]: e.target.value }))}
                  >
                    {currentValue && <option value={currentValue}>{currentValue}</option>}
                  </SelectInput>
                )}
                {option.is_custom && currentValue !== FACTORY_VALUE && (
                  <span className="text-xs text-label-secondary">Abweichend von der Werkseinstellung</span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </Card>
  );
}
